import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const hasCalledAPI = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token || hasCalledAPI.current) return;
    
    hasCalledAPI.current = true;
    
    const verifyEmail = async () => {
      try {
        console.log('Calling verification API with token:', token);
        console.log('API URL:', `${API_ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${token}`);
        const response = await axios.get(`${API_ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${token}`);
        console.log('Verification API response:', response.data);
        setStatus('success');
        setMessage(response.data.message);
        setUser(response.data.user);
      } catch (error: any) {
        console.error('Verification API error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        setStatus('error');
        setMessage(error.response?.data?.message || 'Có lỗi xảy ra khi xác thực email.');
      }
    };
    
    verifyEmail();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login', { 
      state: { 
        message: 'Tài khoản đã được xác thực thành công! Bạn có thể đăng nhập ngay bây giờ.' 
      }
    });
  };

  const handleGoToHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {status === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Đang xác thực tài khoản...
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Vui lòng chờ trong giây lát
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <svg width="30" height="30" fill="white" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </Box>
              
              <Typography variant="h5" gutterBottom color="success.main">
                Xác thực thành công!
              </Typography>
              
              <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                {message}
              </Alert>

              {user && (
                <Box sx={{ width: '100%', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Thông tin tài khoản:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {user.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Số coins: {user.coins} coins
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGoToLogin}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Đăng nhập ngay
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleGoToHome}
                >
                  Về trang chủ
                </Button>
              </Box>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: '#f44336',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <svg width="30" height="30" fill="white" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </Box>
              
              <Typography variant="h5" gutterBottom color="error.main">
                Xác thực thất bại
              </Typography>
              
              <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                {message}
              </Alert>

              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                Nếu bạn gặp vấn đề, vui lòng liên hệ admin để được hỗ trợ.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGoToLogin}
                >
                  Đăng nhập
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleGoToHome}
                >
                  Về trang chủ
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerification; 